import { ChangeEvent } from 'react';
import styled from 'styled-components';
import { IMachineListMachine } from '../../../../interfaces/Dictionary.interfaces';
import { SlideModalSmoother } from '../../../abstract/modal/slide/SlideModalSmoother';
import { BasicInput } from '../../../common/inputs/BasicInput';

interface SearchModelModalProps {
  isSlideModalOpen: boolean;
  closeAll: () => void;
  model: string | undefined;
  machines: IMachineListMachine[] | undefined;
  changeMachine?: (newMachine: IMachineListMachine) => void;
  machineSearchTerm: string | undefined;
  changeMachineSearchTerm: (newSearchTerm: string) => void;
}

// TODO: 모델 선택시 해당 모델의 머신 정보 전체 넘겨주도록 리팩토링. 모델만 선택하는 로직 삭제 필요
export const SearchModelModal = ({
  isSlideModalOpen,
  closeAll,
  machines,
  model,
  changeMachine,
  machineSearchTerm,
  changeMachineSearchTerm,
}: SearchModelModalProps) => {
  const closeModal = () => {
    changeMachineSearchTerm(model ?? '');
    closeAll();
  };

  const handleConfirmButtonClick = () => {
    const selectedMachine =
      machines?.find((machine) => machine.model === machineSearchTerm) ??
      ({ model: machineSearchTerm } as IMachineListMachine);
    changeMachine?.(selectedMachine);
    closeAll();
  };

  const handleMachineItemClick = (selectedMachine: IMachineListMachine) => {
    changeMachine?.(selectedMachine);
    closeAll();
  };

  const handleInputSubmit = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !!machineSearchTerm) {
      handleConfirmButtonClick();
    }
  };

  const splitModelByCharacter = (model: string, keyword: string): { text: string; isMatched: boolean }[] | null => {
    if (!model || !keyword) {
      return null;
    }

    const lowerCaseModel = model.toLocaleLowerCase();
    const lowerCaseKeyword = keyword.toLocaleLowerCase();

    if (!lowerCaseModel.includes(lowerCaseKeyword)) {
      return null;
    }

    const matchedCharacterFirstIndex = lowerCaseModel.search(lowerCaseKeyword);
    const matchedCharacterLastIndex = matchedCharacterFirstIndex + lowerCaseKeyword.length;

    return model.split('').map((char, index) => {
      return {
        text: char,
        isMatched: index >= matchedCharacterFirstIndex && index < matchedCharacterLastIndex,
      };
    });
  };

  return (
    <SlideModalSmoother
      targetState={isSlideModalOpen}
      openDelayTime={0}
      height={'100%'}
      maxHeight={'100%'}
      hasRoundBorder={false}
      handleCloseButtonClick={closeModal}
      padding={'0px'}
      gap={'0px'}
    >
      <Container>
        <InputSection>
          <Input
            value={machineSearchTerm}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const filteredValue = event.currentTarget.value.replace(/[^A-Za-z0-9\-_.\s()]/g, '');
              changeMachineSearchTerm(filteredValue.toUpperCase());
            }}
            placeholder={'모델명을 입력해 주세요. 예) 6155R'}
            onKeyDown={handleInputSubmit}
          />
          <ConfirmButton
            onClick={() => !!machineSearchTerm && handleConfirmButtonClick()}
            disabled={!machineSearchTerm}
          >
            <ConfirmButtonText>{'입력 완료'}</ConfirmButtonText>
          </ConfirmButton>
        </InputSection>
        {machines && machines.length > 0 && (
          <MachineList>
            {machines.map((machine) => (
              <MachineItem key={machine.id} onClick={() => handleMachineItemClick(machine)}>
                {!machineSearchTerm && <MachineTitle>{machine.model}</MachineTitle>}

                {machineSearchTerm && (
                  <MachineTitle>
                    {splitModelByCharacter(machine.model, machineSearchTerm)?.map((char, index) => {
                      if (char.isMatched) {
                        return <MatchedText>{char.text}</MatchedText>;
                      }

                      return <>{char.text}</>;
                    })}
                  </MachineTitle>
                )}

                <MachineSelectButton>
                  <MachineSelectButtonText>{'선택하기'}</MachineSelectButtonText>
                </MachineSelectButton>
              </MachineItem>
            ))}
          </MachineList>
        )}
      </Container>
    </SlideModalSmoother>
  );
};

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  align-items: center;
`;

const InputSection = styled.div`
  display: flex;
  padding: 24px var(--radius-large, 16px);
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const Input = styled(BasicInput)`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Body/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 21px */
`;

const ConfirmButton = styled.button`
  display: flex;
  max-height: 40px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 4px;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Color-primary-blue, #196eff);

  &:disabled {
    opacity: 0.3;
  }
`;

const ConfirmButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-white, #fff);

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */

  white-space: nowrap;
`;

const MachineList = styled.div`
  display: flex;
  padding: 0px var(--radius-large, 16px);
  flex-direction: column;
  align-items: center;
  flex: 1 0 0;
  align-self: stretch;

  overflow-y: scroll;
`;

const MachineItem = styled.div`
  display: flex;
  padding: var(--radius-large, 16px) 0px;
  align-items: center;
  align-self: stretch;

  border-bottom: 1px solid var(--Light-Scheme-Monochrome-grey10, #f2f2f7);

  cursor: pointer;
`;

const MachineTitle = styled.span`
  flex: 1 0 0;

  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* Body/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
`;

const MatchedText = styled.span`
  color: #232429;
`;

const MachineSelectButton = styled.button`
  display: flex;
  padding: 6px 8px;
  align-items: center;
  gap: 2px;

  border-radius: var(--radius-round, 80px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const MachineSelectButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  text-align: center;

  /* System/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 10.2px */
`;
